import React, { useMemo, useRef, useState } from "react";

import ReactGA from "react-ga4";

import noStories from "images/no-stories.png";

import { Button, classnames, Icon, PlainButton } from "@culturehq/components";
import LightboxStories from "components/common/gallery/LightboxStories";
import { t, Trans } from "app/utils";
import styles from "./story-slider.module.scss";

const playIcon = {
  height: "40px",
  width: "40px"
};

const backgroundEffect = {
  alignItems: "center",
  borderRadius: "6px",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "center"
};

const cardTitleContainer = {
  background: "linear-gradient(0deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0))",
  bottom: 0,
  left: 0,
  padding: "30px 15px 15px",
  position: "absolute",
  right: 0
};

const cardTitle = {
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: "2",
  color: "#FFFFFF",
  display: "-webkit-box",
  fontSize: "24px",
  fontWeight: "600",
  lineHeight: "normal",
  margin: "0",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "initial"
};

const chqTmb = {
  backgroundPosition: "50%",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  borderRadius: "25px",
  display: "inline-block",
  height: "45px",
  marginRight: "10px",
  width: "45px",
  minWidth: "45px"
};

const creatorTitle = {
  overflow: "hidden",
  overflowWrap: "break-word",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  wordBreak: "break-all"
};

const creatorName = {
  width: "230px",
  fontSize: "16px",
  fontWeight: "600",
  marginBottom: "3px",
  overflow: "hidden",
  overflowWrap: "break-word",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  wordBreak: "break-all"
};

const creatorContainer = {
  alignItems: "center",
  background: "linear-gradient(180deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0))",
  color: "#fff",
  display: "flex",
  fontSize: "16px",
  fontWeight: "300",
  left: 0,
  padding: "15px 15px 30px",
  position: "absolute",
  right: 0,
  top: 0
};

const getSlideLayout = (
  index,
  containerRef,
  sliderRef,
  stories
) => {
  const STORY_WIDTH = 200;
  let percent = index * (100 / (stories.length + 1));
  const left = index !== 0;
  let right = index !== stories.length && stories.length > 4;

  const container = containerRef.current;
  const slider = sliderRef.current;
  const sliderWidth = STORY_WIDTH * (stories.length + 1);

  if (!(container instanceof HTMLDivElement) || !(slider instanceof HTMLDivElement)) {
    return { percent, left, right };
  }

  const pixels = (percent * slider.clientWidth) / 100;
  const correction = pixels - (slider.clientWidth - container.clientWidth);

  if (sliderWidth <= container.clientWidth) {
    right = false;
  }

  if (correction > 0) {
    percent -= (correction / slider.clientWidth) * 100;
    right = false;
  }

  return { percent, left, right };
};

const SelectStoryButton = ({ storyId, selectedStories, onSelectSingleStory }) => {
  const isSelected = useMemo(
    () => selectedStories.includes(storyId), [storyId, selectedStories]);

  return (
    <Button
      inverted={isSelected}
      className={styles.singleSelectionButton}
      onClick={e => {
        e.stopPropagation();
        onSelectSingleStory(storyId);
      }}
    >
      {isSelected ? "Selected" : "Select"}
    </Button>
  );
};

const StoriesSlider = ({
  admin = false,
  language,
  organization,
  orgName,
  redirectCloseAction = true,
  stories = [],
  selectedStories = [],
  onSelectSingleStory
}) => {
  const [index, setIndex] = useState(0);
  const [activeStory, setActiveStory] = useState(undefined);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const onNext = () => {
    setIndex(value => value + 1);
    ReactGA.event({
      category: "Stories",
      action: "Viewing stories",
      label: `ORGANIZATION=${organization}&URL=${window.location.href}&TYPE=carousel`,
      value: 1
    });
  };
  const onPrev = () => {
    setIndex(value => value - 1);
    ReactGA.event({
      category: "Stories",
      action: "Viewing stories",
      label: `ORGANIZATION=${organization}&URL=${window.location.href}&TYPE=carousel`,
      value: 1
    });
  };

  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const slideLayout = getSlideLayout(index, containerRef, sliderRef, stories);

  const handleThumbnailClick = goToIndex => {
    let target = goToIndex;

    if (goToIndex === -1) {
      target = stories.length - 1;
    }

    if (goToIndex > stories.length - 1) {
      target = 0;
    }

    if (!admin) {
      ReactGA.event({
        category: "Stories",
        action: "Opening a story",
        label: `ORGANIZATION=${organization}&STORY=${stories[target].id}&URL=${window.location.href}&TYPE=carousel`,
        value: stories[target].id
      });
    }

    setActiveStory(stories[target]);
    setModalIsOpen(true);
  };

  const handleClose = () => {
    setModalIsOpen(false);
  };

  const cardWidth = () => {
    if (stories.length >= 3) {
      return "300px";
    }

    if (stories.length === 2) {
      return "50%";
    }

    return "100%";
  };

  const maxCardWidth = () => {
    if (stories.length === 1) {
      return "750px";
    }

    return "100%";
  };

  const backgroundImage = story => {
    if (stories.length >= 2) {
      if (story.media.thumbnail?.endsWith("200")) {
        return story.media.smallUrl || story.media.thumbnail.replace("200", "400");
      }
      return story.media.smallUrl || story.media.thumbnail || story.media.url;
    }

    return story.media.fullUrl || story.media.thumbnail || story.media.url;
  };

  return (
    <>
      {activeStory && (
        <LightboxStories
          modalIsOpen={modalIsOpen}
          activeStory={activeStory}
          currentUserAnswered
          language={language}
          stories={stories}
          onClose={handleClose}
          onStoryChange={handleThumbnailClick}
          noActions
          landingPage
          hideShareLink
          redirectCloseAction={redirectCloseAction}
          orgName={orgName}
        />
      )}
      {stories.length === 0 && (
        <div className="Preview-Browser__Trends">
          <div className="Stories__Empty">
            <img className="Stories__Empty--image" src={noStories} alt={t("Empty Stories")} />
            <div>
              <p className="Stories__Empty--paragraph"><Trans>No stories have been approved for this prompt yet.</Trans></p>
            </div>
          </div>
        </div>
      )}
      <div style={{ position: "relative" }}>
        <button
          aria-label={t("Previous")}
          type="button"
          className={classnames(styles.direction, styles.leftOutside)}
          onClick={onPrev}
          disabled={!slideLayout.left}
        >
          <Icon icon="chevron-left" />
        </button>
        <button
          aria-label={t("Next")}
          type="button"
          className={classnames(styles.direction, styles.rightOutside)}
          onClick={onNext}
          disabled={!slideLayout.right}
        >
          <Icon icon="chevron-right" />
        </button>
        <div ref={containerRef} className={`${stories.length > 0 && styles.biggerContainer} no-scrollbar`}>
          <div
            ref={sliderRef}
            className={styles.slider}
            style={{ transform: `translateX(-${slideLayout.percent}%)` }}
          >
            {stories.map((story, storyIndex) => (
              <PlainButton
                aria-labelledby={story.id}
                key={story.id}
                className={styles.biggerCard}
                style={{ backgroundImage: `url(${backgroundImage(story)})`, width: cardWidth(), maxWidth: maxCardWidth() }}
                onClick={() => handleThumbnailClick(storyIndex)}
              >
                <div id={story.id} style={{ display: "none" }}>{story.question.question}</div>
                <div style={{ ...backgroundEffect, height: "100%" }}>
                  <div style={creatorContainer}>
                    <div style={{ ...chqTmb, backgroundImage: `url(${story.creator.avatar.thumbUrl})` }} />
                    <div style={{ width: "80%" }}>
                      <p style={{ margin: "0", ...creatorName }}>{story.creator.name}</p>
                      <p style={{ margin: "0", ...creatorTitle }}>{story.creator.title}</p>
                    </div>
                  </div>
                  <div style={cardTitleContainer}>
                    <p style={cardTitle}>{story.question.question}</p>
                  </div>
                  {story.media.mediaType === "video" && (
                    <svg style={playIcon} aria-hidden="true" role="presentation" width="14px" height="14px" viewBox="0 0 264 264">
                      <path transform="translate(0 0)" style={{ fill: "#FFFFFF" }} d="M238.163,115.57l-68.127-39.741c-15.201-8.899-40.064-23.393-55.296-32.256L44.115,3.831 C28.919-5.067,13.974,2.07,13.974,19.698v224c0,17.567,14.945,24.735,30.147,15.872l69.376-39.741 c15.232-8.863,40.735-23.357,55.936-32.256l68.449-39.741C253.047,138.933,253.334,124.433,238.163,115.57z" />
                    </svg>
                  )}
                  {onSelectSingleStory && (
                    <SelectStoryButton
                      storyId={story.id}
                      selectedStories={selectedStories}
                      onSelectSingleStory={onSelectSingleStory}
                    />
                  )}
                </div>
              </PlainButton>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default StoriesSlider;
